import { InfiniteScroll, NavBar, Skeleton, Toast } from "antd-mobile";
import { RightOutline } from "antd-mobile-icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getQualityDashboard,
  getQualityRecords,
  MobileQualityDashboard,
  MobileQualityRecord,
  QualityQueue,
} from "../../../api/mobileQuality";
import { createQualityReviewContext } from "../../../utils/mobileQuality";
import styles from "./index.module.css";

const emptyDashboard = (date: string): MobileQualityDashboard => ({
  date,
  capturedCount: 0,
  evaluatedCount: 0,
  qualifiedCount: 0,
  unqualifiedCount: 0,
  captureIssueCount: 0,
  pendingReviewCount: 0,
  qualifiedRate: 0,
});

const HisDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const date = searchParams.get("date") || dayjs().format("YYYY-MM-DD");
  const storeId = searchParams.get("storeId") || "";
  const dishId = searchParams.get("dishId") || "";
  const [dashboard, setDashboard] = useState<MobileQualityDashboard>(() => emptyDashboard(date));
  const [queue, setQueue] = useState<Extract<QualityQueue, "QUALIFIED" | "UNQUALIFIED">>("UNQUALIFIED");
  const [records, setRecords] = useState<MobileQualityRecord[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getQualityDashboard({ date, storeId, dishId })
      .then((result) => result && setDashboard(result))
      .catch(() => Toast.show({ content: "当日品质概览加载失败" }));
  }, [date, storeId, dishId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPage(1);
    setRecords([]);
    getQualityRecords({ date, queue, page: 1, pageSize: 20, storeId, dishId })
      .then((result) => {
        if (!active || !result) return;
        setRecords(result.records || []);
        setHasMore((result.current || 1) < (result.pages || 0));
      })
      .catch(() => active && Toast.show({ content: "当日品质记录加载失败" }))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [date, queue, storeId, dishId]);

  const loadMore = async () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    const result = await getQualityRecords({ date, queue, page: nextPage, pageSize: 20, storeId, dishId });
    if (!result) return;
    setRecords((current) => [...current, ...(result.records || [])]);
    setPage(nextPage);
    setHasMore(nextPage < (result.pages || 0));
  };

  const queueCount = queue === "QUALIFIED" ? dashboard.qualifiedCount : dashboard.unqualifiedCount;
  const tone = queue === "QUALIFIED" ? "mint" : "coral";

  return (
    <main className={styles.page}>
      <NavBar onBack={() => navigate(-1)} className={styles.nav}>
        <span>{dayjs(date).format("M月D日")} 品质记录</span>
      </NavBar>

      <section className={styles.overview}>
        <div><span>出餐</span><strong>{dashboard.capturedCount}</strong></div>
        <div><span>不达标</span><strong className={styles.coralText}>{dashboard.unqualifiedCount}</strong></div>
        <div><span>无效图片</span><strong>{dashboard.captureIssueCount}</strong></div>
        <em>{dashboard.qualifiedRate.toFixed(1)}% 达标</em>
      </section>

      <div className={styles.resultSwitch} role="tablist" aria-label="查看品质结果">
        <button className={queue === "UNQUALIFIED" ? styles.activeCoral : ""} onClick={() => setQueue("UNQUALIFIED")}>不达标 {dashboard.unqualifiedCount}</button>
        <button className={queue === "QUALIFIED" ? styles.activeMint : ""} onClick={() => setQueue("QUALIFIED")}>达标 {dashboard.qualifiedCount}</button>
      </div>

      <section className={styles.recordsSection}>
        <div className={styles.sectionTitle}>
          <h2><i className={styles[tone]} />{queue === "QUALIFIED" ? "达标记录" : "不达标记录"}</h2>
          <span>{queueCount} 条 · 按时间倒序</span>
        </div>
        {loading ? <Skeleton.Paragraph lineCount={6} animated /> : records.length === 0 ? (
          <div className={styles.empty}>当天没有{queue === "QUALIFIED" ? "达标" : "不达标"}记录</div>
        ) : (
          <div className={styles.recordList}>
            {records.map((record, index) => (
              <button
                key={record.id}
                className={styles.record}
                onClick={() => navigate(`/quality/${record.id}`, {
                  state: {
                    qualityReviewContext: createQualityReviewContext(
                      records.map((item) => item.id),
                      index,
                      { date, queue, pageSize: 20, storeId, dishId },
                      page + 1,
                      hasMore
                    ),
                  },
                })}
              >
                <img src={record.captureImageUrl || ""} alt={`${record.dishName || "菜品"}实拍`} />
                <span className={styles.recordCopy}>
                  <strong>{record.dishName || "待识别菜品"}</strong>
                  <small>{record.storeName || "-"}</small>
                  <em className={styles[tone]}>{queue === "QUALIFIED" ? "达标" : "不达标"}{record.aiReason ? ` · ${record.aiReason}` : ""}</em>
                </span>
                <span className={styles.recordMeta}>
                  <time>{record.capturedAt ? dayjs(record.capturedAt).format("HH:mm") : "-"}</time>
                  <RightOutline />
                </span>
              </button>
            ))}
            <InfiniteScroll loadMore={loadMore} hasMore={hasMore} />
          </div>
        )}
      </section>
    </main>
  );
};

export default HisDetail;
